// Video Service für Frontend
import axios, { AxiosInstance } from 'axios';
import { VideoContent, VideoContentData, VideoFile, VideoFilters, VideoFormData, PaginatedResponse, ApiPagination } from '../types/video';
import { normalizePagination } from '../types';
import { getApiBaseUrl } from '../utils/apiConfig';
import { getAccessToken } from '../lib/authToken';

/**
 * Intro-Ende aus dem Formular-Roh-String zur Zahl — oder `null`, wenn nichts drinsteht.
 *
 * ⚠️ Die Leer-Prüfung VOR der Umwandlung ist der ganze Punkt: `Number('')` ist 0, und 0 wäre eine
 * gültige Marke bei Sekunde null. Ein Titel ohne Vorspann bekäme so einen Knopf „Intro
 * überspringen", der nichts überspringt. Leer heißt leer, also `null`.
 */
function introMarkeAusFormular(roh: unknown): number | null {
  if (typeof roh === 'number') return Number.isFinite(roh) ? roh : null;
  if (typeof roh !== 'string') return null;
  const text = roh.trim();
  if (!text) return null;
  const zahl = Number(text);
  return Number.isFinite(zahl) ? zahl : null;
}

class VideoService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = getApiBaseUrl();
    
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      async (config) => {
        const token = await getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  // Rohform einer Video-Zeile, wie sie hier durchläuft: `videoFile` kann oben ODER in
  // `contentData` stehen (oder gar nicht) — genau diese Lücke gleicht normalizeVideoContent
  // aus. `VideoContentData` kennt `videoFile` nicht, weil es dort erst von dieser Methode
  // hineingeschrieben wird; deshalb hier lokal ergänzt statt in types/video.ts.
  private normalizeVideoContent(
    content: VideoContent & { contentData?: VideoContentData & { videoFile?: VideoFile } }
  ): VideoContent {
    if (!content) return content;

    if (!content.videoFile && content.contentData?.videoFile) {
      content.videoFile = content.contentData.videoFile;
    }

    if (content.videoFile && !content.contentData) {
      content.contentData = { videoFile: content.videoFile };
    } else if (content.videoFile && content.contentData && !content.contentData.videoFile) {
      content.contentData.videoFile = content.videoFile;
    }

    return content as VideoContent;
  }

  // Get all video content
  async getVideoContent(filters: VideoFilters): Promise<PaginatedResponse<VideoContent>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    const response = await this.api.get<{ success: boolean; data: { content: VideoContent[]; pagination: ApiPagination } }>(
      `/api/video?${params.toString()}`
    );

    if (response.data.success && response.data.data) {
      return {
        data: (response.data.data.content || []).map(item => this.normalizeVideoContent(item)),
        pagination: normalizePagination(response.data.data.pagination)
      };
    }

    return { data: [], pagination: normalizePagination(null) };
  }

  // Get video content by ID
  async getVideoContentById(id: string): Promise<VideoContent> {
    const response = await this.api.get<{ success: boolean; data: VideoContent }>(`/api/video/${id}`);
    if (response.data.success && response.data.data) {
      return this.normalizeVideoContent(response.data.data);
    }
    throw new Error('Failed to get video content');
  }

  // Create video content
  async createVideoContent(data: VideoFormData): Promise<VideoContent> {
    // Transform VideoFormData to backend format
    const transformedData = this.transformFormDataToBackendFormat(data);
    
    const response = await this.api.post<{ success: boolean; data?: VideoContent }>('/api/video', transformedData);
    if (response.data.success) {
      if (response.data.data) {
        return this.normalizeVideoContent(response.data.data);
      }
      // Backend hat 201 geliefert, Body unvollständig – trotzdem als Erfolg werten, Liste wird neu geladen
      return {} as VideoContent;
    }
    throw new Error('Failed to create video content');
  }

  // Update video content
  async updateVideoContent(id: string, data: Partial<VideoFormData>): Promise<VideoContent> {
    // Transform VideoFormData to backend format
    const transformedData = this.transformFormDataToBackendFormat(data as VideoFormData);
    
    const response = await this.api.put<{ success: boolean; data: VideoContent }>(`/api/video/${id}`, transformedData);
    if (response.data.success && response.data.data) {
      return this.normalizeVideoContent(response.data.data);
    }
    throw new Error('Failed to update video content');
  }

  // Delete video content
  async deleteVideoContent(id: string): Promise<void> {
    const response = await this.api.delete<{ success: boolean; message?: string }>(`/api/video/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete video content');
    }
  }

  // Transform form data to backend format
  private transformFormDataToBackendFormat(formData: VideoFormData) {
    return {
      category: formData.category,
      title: formData.title || '',
      description: formData.description !== undefined ? formData.description : '',
      shortDescription: formData.shortDescription !== undefined ? formData.shortDescription : '',
      // ⛔ `longDescription` gibt es nicht mehr (Bereinigung 2026-09-01, CONTRACT in #769).
      //
      // ⚠️ Die Zeile fehlt hier mit Absicht und darf nicht „nachgetragen" werden: hier stand
      // `formData.longDescription !== undefined ? … : ''`. Ein nicht geführtes Feld hätte damit
      // eine leere Zeichenkette geschickt — solange die Spalte noch existierte, war das eine
      // Löschfalle; jetzt wäre es das Wiederanlegen einer dritten Wahrheit. Das Backend hält mit
      // `services/__tests__/longDescriptionAusgetragen.test.ts` dagegen.
      duration: formData.duration !== undefined ? formData.duration : '',
      headphones: formData.headphones !== undefined ? formData.headphones : 'NO',
      // Stichworte: NUR mitschicken, wenn das Formular sie führt.
      //
      // ⚠️ Hier stand `formData.keywords !== undefined ? formData.keywords : ''` — und seit das
      // Eingabefeld aus VideoForm entfernt wurde, ist `formData.keywords` IMMER undefined. Jedes
      // Speichern schickte also ein ausdrückliches '' und leerte metadata.keywords. Am echten
      // Datensatz passiert: „Fülle, Geldfluss, Wohlstand, Sicherheit" ist weg. Der Merge-Erhalt im
      // Backend kann das nicht auffangen — ein gesendetes '' IST dort eine Löschung, und genau so
      // soll es bleiben. Ein FEHLENDES Feld dagegen lässt den Bestand stehen.
      // Die Suche durchsucht das Feld weiterhin (buildVideoWhere), es ist also kein toter Wert.
      ...(formData.keywords !== undefined ? { keywords: formData.keywords } : {}),
      topicCategories: Array.isArray(formData.topicCategories) ? formData.topicCategories : [],
      // Affirmationen: Textarea (eine pro Zeile) → string[] (leere Zeilen raus) → metadata.affirmations.
      // Wortgleich zu audioService — beide Seiten müssen dasselbe Format liefern, sonst liest das
      // Backend bei Video etwas anderes als bei Audio.
      affirmations:
        typeof formData.affirmations === 'string'
          ? formData.affirmations.split('\n').map((s) => s.trim()).filter(Boolean)
          : Array.isArray(formData.affirmations)
            ? formData.affirmations
            : [],
      // Intro-Ende (Sekunden) → metadata.introEndSeconds. Leeres Feld wird bewusst als `null`
      // gesendet und nicht weggelassen: nur ein ausdrückliches null entfernt eine einmal gesetzte
      // Marke wieder (das Backend erhält ein FEHLENDES Feld, siehe dessen Merge-Erhalt).
      introEndSeconds: introMarkeAusFormular(formData.introEndSeconds),
      videoUrl: formData.videoUrl !== undefined ? formData.videoUrl : '',
      thumbnailUrl: formData.thumbnailUrl !== undefined ? formData.thumbnailUrl : '',
      coverImageUrl: formData.coverImageUrl !== undefined ? formData.coverImageUrl : '',
      // Metadaten - explizit prüfen, ob sie gesetzt wurden
      artist: formData.artist !== undefined ? formData.artist : '',
      album: formData.album !== undefined ? formData.album : '',
      language: formData.language !== undefined ? formData.language : 'de',
      // Relations — `artistId` ist die eigentliche Zuordnung; `artist` oben ist nur der
      // Anzeige-Spiegel, den das Backend daraus ableitet.
      artistId: formData.artistId !== undefined ? formData.artistId : null,
      // Nur mitschicken, wenn das Formular sie wirklich fuehrt. Sonst ging hier bei JEDEM
      // Speichern eine leere Liste raus und loeschte den Bestand — dieselbe Bauart wie bei den
      // Stichwoertern: das Formular kennt die Felder nicht, der Lade-Mapper laedt sie nicht, und
      // die `: []`-Verkuerzung machte aus "nicht gefuehrt" ein "ausdruecklich leer".
      ...(Array.isArray(formData.playlistIds) ? { playlistIds: formData.playlistIds } : {}),
      ...(Array.isArray(formData.productIds) ? { productIds: formData.productIds } : {}),
      isPremium: Boolean(formData.isPremium),
      tags: Array.isArray(formData.tags) ? formData.tags : [],
      // Status nur, wenn das Formular ihn führt. Hier stand `status: … || 'DRAFT'` — dieselbe Bauart, die
      // im Backend still depublizierte (`status-fehlt-faellt-auf-draft`): aus „nicht gesendet" wurde
      // „ausdrücklich Entwurf". Ein FEHLENDER Status lässt den Bestand stehen; beim Anlegen setzt das
      // Backend DRAFT selbst. (BACKLOG `cms-status-oder-draft`)
      ...(formData.status ? { status: formData.status } : {})
    };
  }
}

export const videoService = new VideoService();
